import * as helios from "@hyperionbt/helios";
import * as Sentry from "@sentry/nextjs";
import { SecondaryOfferEntry } from "kreate-common/modules/business-types";
import CircularProgress$Indeterminate from "kreate-common/modules/kreate-ui/components/CircularProgress/components/CircularProgress$Indeterminate";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import { parseKreateUtxoProtocolParams } from "kreate-common/modules/utxo-parser/by-type/protocol-params";
import { buildTx$CancelOffer } from "kreate-protocol/offchain/tx/cancelOffer";
import { httpGet$GetProtocolParams } from "kreate-server/commands/GetProtocolParams/fetcher";
import { MdFavorite } from "react-icons/md";

import { waitForUtxo } from "@/modules/common-utils/async";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { waitModal } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import ModalProgressV2 from "@/modules/kreate-modals/modals/ModalProgressV2";
import { anticipateSteps } from "@/modules/kreate-modals/modals/ModalProgressV2/utils";
import ModalSuccess from "@/modules/kreate-modals/modals/ModalSuccess";

const steps = anticipateSteps({
  buildingTransaction: 5000,
  waitingForSignatureAndSubmission: 8000,
  waitingForConfirmation: 23000,
});

type Params = {
  cip30Wallet: helios.Cip30Wallet;
  offer: SecondaryOfferEntry;
  txMessage: string;
};

export async function* runFlow$CancelOffer({
  cip30Wallet,
  offer,
  txMessage,
}: Params) {
  const addBreadcrumb = (msg: string) => {
    const terms = ["runFlow$CancelOffer", Date.now(), encodeURIComponent(msg)];
    Sentry.addBreadcrumb({ message: terms.join(" - ") });
  };

  addBreadcrumb("precomputing...");
  yield (
    <ModalV2.Root style={{ backgroundColor: "transparent" }} open>
      <Flex.Row padding="48px" justifyContent="center" alignItems="center">
        <CircularProgress$Indeterminate size="96px" />
      </Flex.Row>
    </ModalV2.Root>
  );

  const utxoProtocolParams = await httpGet$GetProtocolParams(
    "/api/v1/utxo/protocol-params"
  );
  const datumProtocolParams =
    parseKreateUtxoProtocolParams(utxoProtocolParams).datumProtocolParams;

  addBreadcrumb("building transaction...");
  yield (
    <ModalProgressV2
      open
      content="Building transaction..."
      ratio={steps.buildingTransaction.ratio}
      expectedDuration={steps.buildingTransaction.duration}
    />
  );

  const { tx } = await buildTx$CancelOffer(
    cip30Wallet,
    offer.utxo,
    new helios.Address(datumProtocolParams.treasuryAddress),
    txMessage
  );

  addBreadcrumb("waiting for signature and submission...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for signature and submission..."
      ratio={steps.waitingForSignatureAndSubmission.ratio}
      expectedDuration={steps.waitingForSignatureAndSubmission.duration}
    />
  );

  const signatures = await cip30Wallet.signTx(tx);
  tx.addSignatures(signatures);
  const txId = await cip30Wallet.submitTx(tx);

  addBreadcrumb("waiting for confirmation...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for confirmation..."
      ratio={steps.waitingForConfirmation.ratio}
      expectedDuration={steps.waitingForConfirmation.duration}
    />
  );

  await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.eq(txId));
  await shamelesslyRevalidateEverything();

  addBreadcrumb("showing success message");
  yield* waitModal<void>((resolve) => (
    <ModalSuccess
      open
      icon={<MdFavorite />}
      content={`You have cancelled the offer.`}
      onClose={resolve}
      buttons={[{ content: "Close", onClick: "close" }]}
    />
  ));

  Sentry.captureEvent({ message: "runFlow$CancelOffer", level: "info" });
}
